import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Shape of a third-party checkout takeover overlay (Cashfree One-Click Checkout,
 * GoKwik, etc.). Matched structurally rather than by vendor selector so a provider
 * swap doesn't silently reintroduce the bug.
 */
const OVERLAY_MIN_Z_INDEX = 1000;
const OVERLAY_MIN_WIDTH_RATIO = 0.6;
const OVERLAY_MIN_HEIGHT_RATIO = 0.5;

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {MutationObserver | null} */
  #overlayObserver = null;

  /** @type {number | null} */
  #overlayScanFrame = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#stopOverlayWatch);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#stopOverlayWatch);
    this.#stopOverlayWatch();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#startOverlayWatch();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * Starts watching for a third-party checkout overlay while the drawer sits in the
   * browser's top layer.
   *
   * Below `theme-drawer`'s modal breakpoint the panel opens with `showModal()`, which
   * promotes it to the top layer. The top layer paints above the entire normal DOM
   * *regardless of z-index*, so a checkout overlay stays completely hidden behind the
   * drawer even at z-index 2147483647 (Cashfree OCC's value). The shopper taps
   * "Check out", nothing appears to happen, and checkout is unreachable until they
   * close the cart.
   *
   * Above the breakpoint the panel opens with `show()` — not modal, z-index applies,
   * the overlay stacks correctly on its own — so that path is deliberately left alone.
   *
   * Watching for the overlay rather than hooking the checkout click keeps this working
   * no matter how the provider binds its handler, or whether it cancels the event.
   */
  #startOverlayWatch = () => {
    if (this.#overlayObserver) return;

    this.#overlayObserver = new MutationObserver(this.#scheduleOverlayScan);
    this.#overlayObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });
  };

  #stopOverlayWatch = () => {
    this.#overlayObserver?.disconnect();
    this.#overlayObserver = null;

    if (this.#overlayScanFrame !== null) {
      cancelAnimationFrame(this.#overlayScanFrame);
      this.#overlayScanFrame = null;
    }
  };

  /** Coalesces bursts of mutations into a single scan per frame. */
  #scheduleOverlayScan = () => {
    if (this.#overlayScanFrame !== null) return;

    this.#overlayScanFrame = requestAnimationFrame(() => {
      this.#overlayScanFrame = null;
      this.#scanForCheckoutOverlay();
    });
  };

  #scanForCheckoutOverlay() {
    // Only the top layer traps the overlay; above the modal breakpoint z-index
    // already resolves it. Bail without disconnecting — `theme-drawer` re-stacks
    // with close()+showModal(), so `:modal` is transiently false while open.
    if (!this.#isDrawerInTopLayer()) return;

    // Takeover overlays are injected at or near the top of <body>; two levels keeps
    // the scan cheap while still catching wrapper-in-wrapper markup.
    for (const element of document.querySelectorAll('body > *, body > * > *')) {
      if (!this.#isCheckoutOverlay(element)) continue;

      this.#stopOverlayWatch();
      this.#themeDrawer?.close();
      return;
    }
  }

  /**
   * @param {Element} element
   * @returns {boolean} Whether the element looks like a checkout takeover overlay.
   */
  #isCheckoutOverlay(element) {
    if (!(element instanceof HTMLElement)) return false;
    // Never react to the theme's own drawers.
    if (element.closest('theme-drawer')) return false;

    const styles = getComputedStyle(element);
    if (styles.position !== 'fixed') return false;
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;
    if (Number(styles.opacity) === 0) return false;
    // Decorative full-screen layers (glow borders, scrim art) aren't takeovers.
    if (styles.pointerEvents === 'none') return false;

    // `zIndex` is "auto" on unpositioned layers — parseInt gives NaN, and NaN
    // comparisons are always false, so test finiteness explicitly.
    const zIndex = Number.parseInt(styles.zIndex, 10);
    if (!Number.isFinite(zIndex) || zIndex < OVERLAY_MIN_Z_INDEX) return false;

    const rect = element.getBoundingClientRect();
    return (
      rect.width >= window.innerWidth * OVERLAY_MIN_WIDTH_RATIO &&
      rect.height >= window.innerHeight * OVERLAY_MIN_HEIGHT_RATIO
    );
  }

  /** @returns {boolean} Whether the drawer panel is a modal dialog in the top layer. */
  #isDrawerInTopLayer() {
    return Boolean(this.#dialog?.matches(':modal'));
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
